import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Input,
  Textarea,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tooltip,
  Stat,
  StatLabel,
  StatNumber,
  SimpleGrid,
  useColorModeValue,
  Divider,
  Icon,
  Badge,
} from '@chakra-ui/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FiAlertTriangle, FiCheckCircle, FiClock, FiInfo, FiPlay } from 'react-icons/fi'
import {
  findEntriesWithoutPrefix,
  parseMultilineEntries,
} from '@/components/common/triggerInputHelpers'
import {
  orderService,
  type PaymentMonitoringExecutionStatus,
  type PaymentMonitoringTriggerResponse,
} from '@/services/admin/orderService'
import { useToast } from '@/hooks/useToast'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  hasCompletedMonitoringStateChange,
  isOrderQueryForMode,
  observeMonitoringExecution,
} from './monitoringExecutionObserver'

type MonitoringRunPhase = 'observing' | 'completed' | 'timed_out' | 'failed' | 'empty'

type MonitoringRun = {
  trigger: PaymentMonitoringTriggerResponse
  status: PaymentMonitoringExecutionStatus | null
  phase: MonitoringRunPhase
  error?: string
}

export function PaymentDetection() {
  const { t } = useTranslation()
  const { currentEnv } = useNetworkEnvironment()
  const toast = useToast()
  const queryClient = useQueryClient()
  const observerAbortRef = useRef<AbortController | null>(null)
  const currentEnvRef = useRef(currentEnv)
  const invalidatedObservationRef = useRef<string | null>(null)
  currentEnvRef.current = currentEnv
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  
  // Form state
  const [merchantId, setMerchantId] = useState('')
  const [orderIds, setOrderIds] = useState('')
  const [force, setForce] = useState(false)
  
  // Validation errors
  const [merchantIdError, setMerchantIdError] = useState('')
  const [orderIdsError, setOrderIdsError] = useState('')
  
  // Results state
  const [lastResults, setLastResults] = useState<MonitoringRun | null>(null)

  const isValidMerchantIdentifier = (value: string): boolean =>
    value.trim().length === 0 || /^[A-Za-z0-9_-]+$/.test(value.trim())

  const startObservation = useCallback(async (
    trigger: PaymentMonitoringTriggerResponse
  ): Promise<void> => {
    observerAbortRef.current?.abort()

    if (trigger.mode !== currentEnvRef.current) {
      return
    }

    if (trigger.ordersQueued === 0) {
      setLastResults({ trigger, status: null, phase: 'empty' })
      return
    }

    const controller = new AbortController()
    observerAbortRef.current = controller
    setLastResults({ trigger, status: null, phase: 'observing' })

    try {
      const result = await observeMonitoringExecution({
        signal: controller.signal,
        readStatus: signal => orderService.getPaymentMonitoringExecutionStatus(
          trigger,
          trigger.mode,
          signal
        ),
        onStatus: status => {
          if (trigger.mode !== currentEnvRef.current) {
            return
          }
          setLastResults(current => current?.trigger.acceptedAt === trigger.acceptedAt
            ? { ...current, status }
            : current)
          if (
            hasCompletedMonitoringStateChange(status)
            && invalidatedObservationRef.current !== trigger.acceptedAt
          ) {
            invalidatedObservationRef.current = trigger.acceptedAt
            void queryClient.invalidateQueries({
              predicate: query => isOrderQueryForMode(query.queryKey, trigger.mode),
            })
          }
        },
      })

      if (controller.signal.aborted || trigger.mode !== currentEnvRef.current) {
        return
      }

      if (result.timedOut) {
        setLastResults({ trigger, status: result.status, phase: 'timed_out' })
        toast.warning({
          title: t('monitoring.trigger.results.timeoutTitle'),
          description: t('monitoring.trigger.results.timeoutMessage'),
          id: 'payment-monitoring-observation-timeout',
        })
        return
      }

      if (!result.status) {
        throw new Error(t('monitoring.trigger.results.failedMessage'))
      }
      setLastResults({ trigger, status: result.status, phase: 'completed' })
    } catch (error) {
      if (controller.signal.aborted || trigger.mode !== currentEnvRef.current) {
        return
      }
      const message = error instanceof Error
        ? error.message
        : t('monitoring.trigger.results.failedMessage')
      setLastResults(current => ({
        trigger,
        status: current?.trigger.acceptedAt === trigger.acceptedAt ? current.status : null,
        phase: 'failed',
        error: message,
      }))
      toast.warning({
        title: t('monitoring.trigger.results.failedTitle'),
        description: t('monitoring.trigger.results.failedMessage'),
        id: 'payment-monitoring-observation-failed',
      })
    }
  }, [queryClient, t, toast])

  useEffect(() => {
    observerAbortRef.current?.abort()
    invalidatedObservationRef.current = null
    setLastResults(null)
    return () => observerAbortRef.current?.abort()
  }, [currentEnv])
  
  // Mutation for triggering monitoring
  const triggerMutation = useMutation({
    mutationFn: async () => {
      // Validate inputs
      let hasError = false
      
      if (!isValidMerchantIdentifier(merchantId)) {
        setMerchantIdError(t('monitoring.trigger.errors.invalidMerchantId'))
        hasError = true
      } else {
        setMerchantIdError('')
      }
      
      // Validate order IDs if provided
      let parsedOrderIds: string[] | undefined
      if (orderIds.trim()) {
        parsedOrderIds = parseMultilineEntries(orderIds)
        
        const invalidOrderIds = findEntriesWithoutPrefix(parsedOrderIds, 'ord_')
        if (invalidOrderIds.length > 0) {
          setOrderIdsError(t('monitoring.trigger.errors.invalidOrderIds', { 
            ids: invalidOrderIds.join(', ') 
          }))
          hasError = true
        } else {
          setOrderIdsError('')
        }
      } else {
        setOrderIdsError('')
      }
      
      if (hasError) {
        throw new Error(t('monitoring.trigger.errors.validationFailed'))
      }
      
      // Build request params
      const params: any = {}
      const normalizedMerchantId = merchantId.trim()
      if (normalizedMerchantId) params.merchantId = normalizedMerchantId
      if (parsedOrderIds && parsedOrderIds.length > 0) params.orderIds = parsedOrderIds
      if (force) params.force = force
      
      const requestedMode = currentEnv
      const data = await orderService.triggerPaymentMonitoring(params, requestedMode)
      return { data, requestedMode }
    },
    onSuccess: ({ data, requestedMode }) => {
      if (requestedMode !== currentEnvRef.current) {
        return
      }
      if (data.mode !== requestedMode) {
        setLastResults({
          trigger: data,
          status: null,
          phase: 'failed',
          error: t('monitoring.trigger.results.modeMismatchMessage'),
        })
        return
      }
      void startObservation(data)
      toast.success({
        title: t('monitoring.trigger.success.title'),
        description: data.message || t('monitoring.trigger.success.description'),
        id: 'payment-monitoring-success', // Unique ID to prevent duplicates
      })
      
      // Clear form on success
      setMerchantId('')
      setOrderIds('')
      setForce(false)
    },
    onError: (error: any) => {
      toast.error({
        title: t('monitoring.trigger.error.title'),
        description: error.message || t('monitoring.trigger.error.description'),
        id: 'payment-monitoring-error', // Unique ID to prevent duplicates
      })
    },
  })
  
  const handleSubmit = () => {
    triggerMutation.mutate()
  }
  
  const handleMerchantIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMerchantId(e.target.value)
    if (merchantIdError) setMerchantIdError('')
  }
  
  const handleOrderIdsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setOrderIds(e.target.value)
    if (orderIdsError) setOrderIdsError('')
  }

  const isObserving = lastResults?.phase === 'observing'
  const isBusy = triggerMutation.isPending || isObserving
  const completedOrders = lastResults?.status?.orders.filter(order => order.complete).length ?? 0
  const stateChanges = lastResults?.status?.orders.filter(order =>
    order.stateVersion !== null && order.stateVersion > order.baselineStateVersion
  ).length ?? 0
  const expiredOrders = lastResults?.status?.orders.filter(
    order => order.outcome === 'expired_unpaid'
  ).length ?? 0
  const pendingOrders = Math.max(
    0,
    (lastResults?.trigger.ordersQueued ?? 0) - completedOrders
  )
  const skippedOrders = lastResults
    ? lastResults.trigger.skipped.recentlyScannedOrderIds.length
      + lastResults.trigger.skipped.notEligibleIdentifiers.length
      + lastResults.trigger.skipped.notFoundIdentifiers.length
    : 0
  
  return (
    <Card bg={cardBg} borderColor={borderColor}>
      <CardHeader>
        <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
          <HStack minW={0} flex="1">
            <Icon as={FiPlay} color="blue.500" />
            <Heading size="md" overflowWrap="anywhere">
              {t('monitoring.trigger.title')}
            </Heading>
          </HStack>
          <Badge colorScheme="blue" variant="subtle">
            {t('monitoring.trigger.badge')}
          </Badge>
        </HStack>
      </CardHeader>
      
      <CardBody>
        <VStack spacing={6} align="stretch">
          {/* Description */}
          <Alert status="info" variant="left-accent" borderRadius="md">
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">{t('monitoring.trigger.info.title')}</AlertTitle>
              <AlertDescription fontSize="xs">
                {t('monitoring.trigger.info.description')}
              </AlertDescription>
            </Box>
          </Alert>
    
          {/* Form Fields */}
          <VStack spacing={4} align="stretch">
            {/* Merchant ID Field */}
            <FormControl isInvalid={!!merchantIdError}>
              <FormLabel fontSize="sm">{t('monitoring.trigger.fields.merchantId.label')}</FormLabel>
              <Input
                placeholder={t('monitoring.trigger.fields.merchantId.placeholder')}
                value={merchantId}
                onChange={handleMerchantIdChange}
                size="sm"
                isDisabled={isBusy}
              />
              <FormHelperText fontSize="xs">
                {t('monitoring.trigger.fields.merchantId.helper')}
              </FormHelperText>
              {merchantIdError && (
                <FormErrorMessage fontSize="xs">{merchantIdError}</FormErrorMessage>
              )}
            </FormControl>
            
            {/* Order IDs Field */}
            <FormControl isInvalid={!!orderIdsError}>
              <FormLabel fontSize="sm">{t('monitoring.trigger.fields.orderIds.label')}</FormLabel>
              <Textarea
                placeholder={t('monitoring.trigger.fields.orderIds.placeholder')}
                value={orderIds}
                onChange={handleOrderIdsChange}
                size="sm"
                rows={3}
                isDisabled={isBusy}
              />
              <FormHelperText fontSize="xs">
                {t('monitoring.trigger.fields.orderIds.helper')}
              </FormHelperText>
              {orderIdsError && (
                <FormErrorMessage fontSize="xs">{orderIdsError}</FormErrorMessage>
              )}
            </FormControl>
            
            {/* Force Option */}
            <FormControl>
              <HStack justify="space-between">
                <HStack>
                  <FormLabel fontSize="sm" mb={0}>
                    {t('monitoring.trigger.fields.force.label')}
                  </FormLabel>
                  <Tooltip 
                    label={t('monitoring.trigger.fields.force.tooltip')}
                    placement="top"
                  >
                    <Box>
                      <Icon as={FiInfo} color="gray.500" boxSize={3} />
                    </Box>
                  </Tooltip>
                </HStack>
                <Switch
                  isChecked={force}
                  onChange={(e) => setForce(e.target.checked)}
                  isDisabled={isBusy}
                  colorScheme="blue"
                />
              </HStack>
              <FormHelperText fontSize="xs">
                {t('monitoring.trigger.fields.force.helper')}
              </FormHelperText>
            </FormControl>
          </VStack>
          
          {/* Submit Button */}
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={triggerMutation.isPending}
            isDisabled={isObserving}
            loadingText={t('monitoring.trigger.button.loading')}
            leftIcon={<FiPlay />}
            size="md"
          >
            {t('monitoring.trigger.button.submit')}
          </Button>
          
          {/* Results Display */}
          {lastResults && (
            <>
              <Divider />
              <VStack spacing={4} align="stretch">
                <HStack>
                  <Icon
                    as={lastResults.phase === 'completed'
                      ? FiCheckCircle
                      : lastResults.phase === 'failed' || lastResults.phase === 'timed_out'
                        ? FiAlertTriangle
                        : FiClock}
                    color={lastResults.phase === 'completed'
                      ? 'green.500'
                      : lastResults.phase === 'failed' || lastResults.phase === 'timed_out'
                        ? 'orange.500'
                        : 'blue.500'}
                  />
                  <Heading size="sm">{t('monitoring.trigger.results.title')}</Heading>
                </HStack>
                
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.ordersQueued')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="blue.500">
                      {lastResults.trigger.ordersQueued}
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.batchesQueued')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="blue.500">
                      {lastResults.trigger.batchesQueued}
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.ordersObserved')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="green.500">
                      {completedOrders}
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.pendingOrders')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="orange.500">
                      {pendingOrders}
                    </StatNumber>
                  </Stat>
                  
                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.stateChanges')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="purple.500">
                      {stateChanges}
                    </StatNumber>
                  </Stat>

                  <Stat>
                    <StatLabel fontSize="xs">
                      {t('monitoring.trigger.results.expiredUnpaid')}
                    </StatLabel>
                    <StatNumber fontSize="2xl" color="orange.500">
                      {expiredOrders}
                    </StatNumber>
                  </Stat>
                </SimpleGrid>

                {lastResults.phase === 'observing' && (
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('monitoring.trigger.results.observingMessage')}
                    </AlertDescription>
                  </Alert>
                )}
                {lastResults.phase === 'completed' && (
                  <Alert status="success" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('monitoring.trigger.results.completedMessage')}
                    </AlertDescription>
                  </Alert>
                )}
                {lastResults.phase === 'empty' && (
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('monitoring.trigger.results.noEligibleMessage')}
                    </AlertDescription>
                  </Alert>
                )}
                {(lastResults.phase === 'timed_out' || lastResults.phase === 'failed') && (
                  <Alert status="warning" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {lastResults.phase === 'timed_out'
                        ? t('monitoring.trigger.results.timeoutMessage')
                        : lastResults.error || t('monitoring.trigger.results.failedMessage')}
                    </AlertDescription>
                  </Alert>
                )}
                {lastResults.trigger.truncated && (
                  <Alert status="warning" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('monitoring.trigger.results.truncatedMessage', {
                        limit: lastResults.trigger.selectionLimit,
                      })}
                    </AlertDescription>
                  </Alert>
                )}
                {skippedOrders > 0 && (
                  <Alert status="info" variant="left-accent" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="xs">
                      {t('monitoring.trigger.results.skippedMessage', { count: skippedOrders })}
                    </AlertDescription>
                  </Alert>
                )}
                {(lastResults.phase === 'timed_out' || lastResults.phase === 'failed') && (
                  <Button
                    variant="outline"
                    colorScheme="blue"
                    onClick={() => void startObservation(lastResults.trigger)}
                    isDisabled={lastResults.trigger.mode !== currentEnv}
                    size="sm"
                  >
                    {t('monitoring.trigger.results.recheck')}
                  </Button>
                )}
              </VStack>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  )
}
